import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import { toast } from "react-toastify";
import { withGoogleReCaptcha } from "react-google-recaptcha-v3";

import Button from "components/Button";
import AuthLayout from "components/layouts/AuthLayout";
import FormikInput from "components/formik/FormikInput";
import PasswordValidator from "components/PasswordValidator";

import api from "libs/api";
import registerSchema from "libs/validations/registerSchema";

const mainClass = "auth-content";

const RegisterContent = ({ setEmailConfirmed, googleReCaptchaProps }) => {
  const { search } = useLocation();
  const query = queryString.parse(search);
  const { t } = useTranslation();

  const [token, setToken] = useState(null);

  const getToken = useCallback(async () => {
    if (!googleReCaptchaProps?.executeRecaptcha) return;

    const _token = await googleReCaptchaProps.executeRecaptcha("register");
    setToken(_token);
  }, [googleReCaptchaProps]);

  useEffect(() => {
    getToken();
  }, [getToken]);

  useEffect(() => {
    if (!query?.ref || Array.isArray(query.ref)) return;

    api.get(`/auth/referral/${query.ref}`).catch(() => {});
  }, [query?.ref]);

  const onSubmit = async (values, setSubmitting, setErrors) => {
    try {
      if (!token) {
        setSubmitting(false);
        return;
      }

      let data_to_send = { ...values, token };

      if (query?.next) {
        data_to_send.next = query?.next;
      }
      if (query?.type === "partner") {
        data_to_send.partner = true;
      }

      await api.post("/auth/register", data_to_send);
      setEmailConfirmed(values?.email);
    } catch (err) {
      if (err?.response?.data?.type === "email_exist") {
        setErrors({ email: err?.response?.data?.error });
      } else {
        toast.error(t("Something went wrong."));
      }
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className={mainClass}>
        <div className={`${mainClass}__wrapper`}>
          <div className={`${mainClass}__header`}>
            <h1>{t("Welcome!")}</h1>
            <span>
              {query?.type === "partner"
                ? t("Promote Webinity and build passive income")
                : t("Start managing your website faster and better")}
            </span>
          </div>
          <Formik
            initialValues={{
              first_name: "",
              last_name: "",
              email: "",
              password: "",
              password2: "",
            }}
            validationSchema={registerSchema}
            onSubmit={(values, { setSubmitting, setErrors }) => {
              onSubmit(values, setSubmitting, setErrors);
            }}
          >
            {({ values, isSubmitting }) => (
              <Form className={`${mainClass}__form`} autoComplete="off">
                <div className={`${mainClass}__form__inputs`}>
                  <FormikInput
                    name="first_name"
                    label={t("Name")}
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="last_name"
                    label={t("Surname")}
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="email"
                    type="email"
                    label={t("Email")}
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="password"
                    label={t("Password")}
                    type="password"
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="password2"
                    label={t("Repeat password")}
                    type="password"
                    disabled={isSubmitting}
                  />
                </div>
                <PasswordValidator password={values?.password} />
                <div className={`${mainClass}__form__actions`}>
                  <Button type="submit" loading={!!isSubmitting} fullWidth>
                    {t("Sign up")}
                  </Button>
                  <span>
                    {t("Do you have an account?")}{" "}
                    <Link to="/login">{t("Sign in")}</Link>
                  </span>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </AuthLayout>
  );
};

export default withGoogleReCaptcha(RegisterContent);
